/**
 * InvoiceActions Component - T104
 *
 * Sidebar action buttons for invoice detail page (PDF, status transitions)
 */
import { useState } from 'react';
import { Button, GlassPanel } from '../ui';
import { PaymentDateModal } from './PaymentDateModal';
import {
  useMarkInvoicePaid,
  useMarkInvoiceSent,
  useMarkInvoiceDeferred,
  useUnDeferInvoice,
  previewInvoicePdf,
  downloadInvoicePdf,
  type Invoice,
} from '../../hooks/useInvoices';
import { useToastHelpers } from '../ui/Toast';

interface InvoiceActionsProps {
  invoice: Invoice;
}

export function InvoiceActions({ invoice }: InvoiceActionsProps) {
  const toast = useToastHelpers();
  const markPaid = useMarkInvoicePaid();
  const markSent = useMarkInvoiceSent();
  const markDeferred = useMarkInvoiceDeferred();
  const unDefer = useUnDeferInvoice();
  const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);

  const today = new Date();
  const defaultPaymentDate = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');

  const isDraft = invoice.status === 'draft';
  const isSent = invoice.status === 'sent';
  const isDeferred = invoice.status === 'deferred';
  const canMarkPaid = ['draft', 'sent', 'overdue', 'deferred'].includes(invoice.status);
  const canDefer = isSent || invoice.status === 'overdue';

  const handleMarkSent = async () => {
    try {
      await markSent.mutateAsync({ id: invoice.id, version: invoice.version });
      toast.success('Rechnung als gesendet markiert');
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleMarkPaid = async (paymentDate: string) => {
    try {
      await markPaid.mutateAsync({ id: invoice.id, version: invoice.version, paymentDate });
      toast.success('Rechnung als bezahlt markiert');
      setShowPaymentDateModal(false);
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleMarkDeferred = async () => {
    try {
      await markDeferred.mutateAsync({ id: invoice.id, version: invoice.version });
      toast.success('Rechnung zurückgestellt');
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleUnDefer = async () => {
    try {
      await unDefer.mutateAsync({ id: invoice.id, version: invoice.version });
      toast.success('Rechnung reaktiviert');
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  return (
    <GlassPanel>
      <h3 className="text-sm font-medium text-white/70 mb-3">Aktionen</h3>
      <div className="space-y-2">
        {/* PDF Actions */}
        <Button
          variant="secondary"
          size="sm"
          className="w-full justify-start"
          onClick={() => previewInvoicePdf(invoice.id)}
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          PDF Vorschau
        </Button>

        <Button
          variant="secondary"
          size="sm"
          className="w-full justify-start"
          onClick={() => downloadInvoicePdf(invoice.id)}
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          PDF herunterladen
        </Button>

        {/* Status transitions */}
        {isDraft && (
          <Button
            size="sm"
            className="w-full justify-start bg-blue-600 hover:bg-blue-700"
            onClick={handleMarkSent}
            loading={markSent.isPending}
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Als gesendet markieren
          </Button>
        )}

        {canMarkPaid && !isDeferred && (
          <Button
            size="sm"
            className="w-full justify-start bg-green-600 hover:bg-green-700"
            onClick={() => setShowPaymentDateModal(true)}
            loading={markPaid.isPending}
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Als bezahlt markieren
          </Button>
        )}

        {canDefer && (
          <Button
            variant="secondary"
            size="sm"
            className="w-full justify-start text-orange-400"
            onClick={handleMarkDeferred}
            loading={markDeferred.isPending}
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Zurückstellen
          </Button>
        )}

        {isDeferred && (
          <>
            <Button
              size="sm"
              className="w-full justify-start bg-blue-600 hover:bg-blue-700"
              onClick={handleUnDefer}
              loading={unDefer.isPending}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reaktivieren
            </Button>
            <Button
              size="sm"
              className="w-full justify-start bg-green-600 hover:bg-green-700"
              onClick={() => setShowPaymentDateModal(true)}
              loading={markPaid.isPending}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Als bezahlt markieren
            </Button>
          </>
        )}
      </div>
      <PaymentDateModal
        isOpen={showPaymentDateModal}
        initialDate={defaultPaymentDate}
        title="Rechnung als bezahlt markieren"
        confirmLabel="Als bezahlt markieren"
        isPending={markPaid.isPending}
        onClose={() => setShowPaymentDateModal(false)}
        onConfirm={handleMarkPaid}
      />
    </GlassPanel>
  );
}
